import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Banner } from 'design-system/src/components/Banner/Banner';
import { RadioBlockGroup, RadioOptionWrapper, RadioLabel } from 'design-system/src/components/Radio/Radio';
import { Checkbox } from '@mui/material';
import { ActionCard } from '../../ActionCard/ActionCard';
import { OtherRequestButton } from './shared/OtherRequestButton';
import { Input } from '../../Input';
import { FormLabel } from '../../FormLabel';
import { DropdownWithPlaceholder, DropdownItem } from '../../Dropdown';
import { client, type BenefitPreferenceType } from '../../../api';
import { tasksQueryKey } from '../../../queries';
import { useSitesOfCare } from '../../../hooks/useBenefitPreferences';
import { formatAddress } from '../../../utils';
import AddSiteOfCareModal from '../../settings/AddSiteOfCareModal';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import type { Task } from '../types';

type BenefitsRoutingSelectionActionsProps = {
  /** The task ID */
  taskId: string;
  /** The full task object */
  task: Task;
  /** Display name of the drug */
  drugName: string;
  /** NDC code of the drug */
  drugNdc?: string;
  /** Callback to show the follow-up task modal */
  setShowNewFollowupTaskModal: (show: boolean) => void;
};

const NPI_LENGTH = 10;

type PreferenceFormData = {
  hcpcs: string;
  cpt: string;
  rendering_provider_npi: string;
  site_of_care_id: string;
  benefits_preference: BenefitPreferenceType | '';
};

type TaskState = 'active' | 'expired' | 'completed';

const getTaskState = (task: Task): TaskState => {
  if (task.is_expired) return 'expired';
  if (task.status === 'completed') return 'completed';
  return 'active';
};

const SETTINGS_LINK = '/settings?tab=benefitPreferences';

const FULFILLMENT_OPTIONS = [
  {
    value: 'buy_and_bill' as const,
    label: 'Buy-and-bill',
    description: 'Your practice purchases and bills the payer directly',
  },
  {
    value: 'white_bagging' as const,
    label: 'Specialty pharmacy (white bagging)',
    description: 'The specialty pharmacy will ship the medication to the site of care',
  },
];

/** Map a fulfillment selection to the backend response type */
const getResponseType = (selection: string): 'process_as_medical_benefits' | 'process_as_pharmacy_benefits' => {
  if (selection === 'buy_and_bill') return 'process_as_medical_benefits';
  return 'process_as_pharmacy_benefits';
};

export const BenefitsRoutingSelectionActions = ({
  taskId,
  task,
  drugName,
  drugNdc,
  setShowNewFollowupTaskModal,
}: BenefitsRoutingSelectionActionsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: sitesData } = useSitesOfCare();
  const sites = sitesData?.sites ?? [];

  const taskState = getTaskState(task);
  const isDisabled = taskState !== 'active';

  // Prefer NDC-resolved drug name from backend handler data, fall back to prescription drug name
  const resolvedDrugName = task.drug_name ?? drugName;

  const [formData, setFormData] = useState<PreferenceFormData>({
    hcpcs: task.hcpcs ?? '',
    cpt: task.cpt ?? '',
    rendering_provider_npi: task.rendering_provider_npi ?? '',
    site_of_care_id: task.site_of_care_id ?? '',
    benefits_preference: task.benefits_preference ?? '',
  });
  const [saveAsPracticePreference, setSaveAsPracticePreference] = useState(task.preference_scope === 'practice');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddSiteOfCareModal, setShowAddSiteOfCareModal] = useState(false);

  const isFormValid = !!(formData.hcpcs && formData.cpt && formData.site_of_care_id && formData.benefits_preference);

  const handleSubmit = async () => {
    if (!isFormValid) return;

    const responseType = getResponseType(formData.benefits_preference);

    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('task_response', responseType);

      const preferenceData = {
        drug_name: resolvedDrugName,
        drug_ndc: drugNdc ?? null,
        hcpcs: formData.hcpcs || null,
        cpt: formData.cpt || null,
        site_of_care_id: formData.site_of_care_id || null,
        benefits_preference: formData.benefits_preference || null,
        provider_npi: task.provider_npi ?? null,
        rendering_provider_npi: formData.rendering_provider_npi || null,
      };

      submitData.append(
        'additional_input',
        JSON.stringify({
          preference_scope: saveAsPracticePreference ? 'practice' : 'prescription',
          preference_data: preferenceData,
        }),
      );

      await client.post(`tasks/${taskId}/respond`, submitData);
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBanner = () => {
    if (taskState === 'completed' && task.completed_at) {
      return (
        <div className="mb-4 rounded-medium bg-surface-200 p-3">
          <Typography>
            This task was completed on <strong>{formatStandardDate(task.completed_at)}</strong>
            {task.completed_by_name && (
              <>
                {' '}
                by <strong>{task.completed_by_name}</strong>
              </>
            )}
            .{' '}
            <Link to={SETTINGS_LINK} className="underline">
              Manage fulfillment preferences
            </Link>
          </Typography>
        </div>
      );
    }

    if (taskState === 'expired') {
      return (
        <div className="mb-4">
          <Banner
            variant="warning"
            description={
              <>
                Provider did not indicate a preference so Tandem defaulted to pursuing specialty pharmacy fulfillment
                (white bagging)
                {task.expired_at && (
                  <>
                    {' '}
                    on <strong>{formatStandardDate(task.expired_at)}</strong>
                  </>
                )}
                .{' '}
                <Link to={SETTINGS_LINK} className="underline">
                  Manage fulfillment preferences
                </Link>
              </>
            }
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderBanner()}

      {taskState === 'active' && (
        <div className="mb-4 flex flex-col gap-1">
          <Typography weight="bold">Set your fulfillment preference for this drug.</Typography>
          <Typography>
            Your practice does not have a fulfillment preference set for this provider-administered drug. Tandem will
            default to pursuing specialty pharmacy fulfillment (white bagging) if you do not respond within 2 days.
          </Typography>
        </div>
      )}

      <ActionCard>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <Input label="Drug" value={resolvedDrugName} disabled />
            </div>

            <div className="flex gap-4">
              <div className="flex w-1/2 flex-col">
                <Input
                  label="HCPCS code"
                  placeholder="e.g. J1745"
                  value={formData.hcpcs}
                  disabled={isDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.toUpperCase().slice(0, 5);
                    setFormData({ ...formData, hcpcs: value });
                  }}
                  maxLength={5}
                />
              </div>

              <div className="flex w-1/2 flex-col">
                <Input
                  label="Admin CPT code"
                  placeholder="e.g. 96413"
                  value={formData.cpt}
                  disabled={isDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData({ ...formData, cpt: value });
                  }}
                  maxLength={5}
                />
              </div>
            </div>

            <div className="flex flex-col">
              <Input
                label="Rendering NPI (optional)"
                placeholder="e.g. 1234567890"
                value={formData.rendering_provider_npi}
                disabled={isDisabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, NPI_LENGTH);
                  setFormData({ ...formData, rendering_provider_npi: value });
                }}
                maxLength={NPI_LENGTH}
              />
            </div>

            <div className="flex flex-col">
              <div className="mb-1 flex items-center justify-between">
                <FormLabel htmlFor="site-of-care" className="mb-0">
                  Site of care
                </FormLabel>
                {!isDisabled && (
                  <button
                    type="button"
                    onClick={() => setShowAddSiteOfCareModal(true)}
                    className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 hover:opacity-80"
                  >
                    <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--purple-700)">
                      + Add site of care
                    </Typography>
                  </button>
                )}
              </div>
              <DropdownWithPlaceholder
                id="site-of-care"
                placeholder="Select site of care"
                value={formData.site_of_care_id}
                onChange={(e) => setFormData({ ...formData, site_of_care_id: e.target.value as string })}
                disabled={isDisabled}
              >
                {sites.map((site) => {
                  const fullAddress = formatAddress(
                    [site.address_line_1, site.address_line_2].filter(Boolean).join(', '),
                    site.city,
                    site.state,
                    site.zipcode,
                  );
                  return (
                    <DropdownItem key={site.id} value={site.id}>
                      {fullAddress}
                    </DropdownItem>
                  );
                })}
              </DropdownWithPlaceholder>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <FormLabel>Primary fulfillment preference</FormLabel>
            <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
              If denied, you will have the option to pursue the other benefit.
            </Typography>
            <RadioBlockGroup $variant="outlined">
              {FULFILLMENT_OPTIONS.map((option) => (
                <RadioOptionWrapper
                  id={`preference-${option.value}`}
                  name="benefits-preference"
                  value={option.value}
                  checked={formData.benefits_preference === option.value}
                  onChange={() =>
                    setFormData({ ...formData, benefits_preference: option.value as BenefitPreferenceType })
                  }
                  key={option.value}
                  variant="outlined"
                  disabled={isDisabled}
                >
                  <div className="flex flex-col gap-0.5">
                    <RadioLabel $disabled={isDisabled}>{option.label}</RadioLabel>
                    <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                      {option.description}
                    </Typography>
                  </div>
                </RadioOptionWrapper>
              ))}
            </RadioBlockGroup>
          </div>

          <div className="flex items-center">
            <Checkbox
              checked={saveAsPracticePreference}
              onChange={(e) => setSaveAsPracticePreference(e.target.checked)}
              disabled={isDisabled}
              size="small"
              sx={{ padding: '0 8px 0 0' }}
            />
            <Typography styledAs="bodySmallDMSans" color={isDisabled ? 'var(--grayscale-500)' : undefined}>
              Save this fulfillment preference for all future patients prescribed this drug in this practice. You can
              change this anytime in Settings.
            </Typography>
          </div>
        </div>

        {taskState === 'active' && (
          <ButtonRow align={Alignment.SpaceBetween} fullWidth>
            <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              disabled={!isFormValid || isSubmitting}
            >
              Submit
            </Button>
          </ButtonRow>
        )}
      </ActionCard>

      <AddSiteOfCareModal
        open={showAddSiteOfCareModal}
        onClose={() => setShowAddSiteOfCareModal(false)}
        onSiteCreated={(siteId) => setFormData((prev) => ({ ...prev, site_of_care_id: siteId }))}
      />
    </>
  );
};
